import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

const SelectClinicModal = ({ open, clinics, onSelect, defaultRoute = 'setup', preventNavigation = false }) => {
  const navigate = useNavigate();
  const params = useParams();
  const { i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [selectedClinicId, setSelectedClinicId] = useState(null);

  useEffect(() => {
    // Reset selection when modal opens
    if (open && clinics && clinics.length > 0) {
      setSelectedClinicId(null);
    }
  }, [open, clinics]);

  if (!open) return null;

  const handleSelect = () => {
    if (!selectedClinicId) return;

    const selectedClinic = clinics.find(c => c.id === parseInt(selectedClinicId));
    if (!selectedClinic) return;

    try {
      // Save to dedicated localStorage key (won't be overwritten by auth hooks)
      localStorage.setItem('selected_clinic_id', selectedClinic.id.toString());

      // Also update user_data for backward compatibility
      const userData = localStorage.getItem("user_data");
      let userDataParsed = {};

      if (userData && userData !== "undefined" && userData !== "null") {
        try {
          userDataParsed = JSON.parse(userData);
        } catch (e) {
          console.error('Error parsing user_data:', e);
          userDataParsed = {};
        }
      }

      // Set clinic_id in user_data
      userDataParsed.clinic_id = selectedClinic.id;
      userDataParsed.clinic = {
        ...userDataParsed.clinic,
        id: selectedClinic.id,
        name: selectedClinic.name_en || selectedClinic.name,
        name_en: selectedClinic.name_en,
        name_ar: selectedClinic.name_ar,
        is_owner: true
      };

      localStorage.setItem("user_data", JSON.stringify(userDataParsed));

      // Store clinic object
      localStorage.setItem(
        "clinic",
        JSON.stringify({
          is_owner: true,
          clinic_id: selectedClinic.id
        })
      );

      // Call onSelect callback
      if (onSelect) {
        onSelect(selectedClinic);
      }

      if (preventNavigation) {
        return;
      }

      // Update URL immediately with new clinicId, preserving current view or using defaultRoute
      const currentIndex = params?.index || defaultRoute;
      navigate(`/medical/clinic/${selectedClinic.id}/${currentIndex}`, { replace: true });
    } catch (error) {
      console.error('Error selecting clinic:', error);
    }
  };

  const getClinicName = (clinic) => {
    if (isRTL && clinic.name_ar) {
      return clinic.name_ar;
    }
    return clinic.name_en || clinic.name || `Clinic ${clinic.id}`;
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        background: "rgba(0,0,0,0.5)",
        zIndex: 2000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backdropFilter: "blur(4px)",
      }}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: 12,
          boxShadow: "0 2px 16px rgba(0,0,0,0.2)",
          minWidth: 400,
          maxWidth: "90vw",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          position: "relative",
          direction: isRTL ? 'rtl' : 'ltr',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header Section - Fixed */}
        <div style={{ padding: "40px 32px 0 32px", flexShrink: 0 }}>
          <h2
            style={{
              fontWeight: 700,
              fontSize: 24,
              marginBottom: 8,
              color: "#22313f",
              textAlign: "center",
            }}
          >
            {isRTL ? "اختر العيادة" : "Select Clinic"}
          </h2>
          <div style={{ fontSize: 17, color: "#444", marginBottom: 32, textAlign: "center" }}>
            {isRTL
              ? "يرجى اختيار عيادة للمتابعة"
              : "Please select a clinic to continue"}
          </div>
        </div>

        {/* Scrollable Clinics List */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "0 32px",
            minHeight: 0,
          }}
        >
          {clinics && clinics.length > 0 ? (
            <div style={{ display: "flex", flexDirection: "column", gap: "12px", paddingBottom: "16px" }}>
              {clinics.map((clinic) => (
                <button
                  key={clinic.id}
                  onClick={() => setSelectedClinicId(clinic.id)}
                  style={{
                    padding: "16px 20px",
                    border: selectedClinicId === clinic.id
                      ? "2px solid #1dd1a1"
                      : "2px solid #e0e0e0",
                    borderRadius: 8,
                    background: selectedClinicId === clinic.id
                      ? "#f0fdfa"
                      : "#fff",
                    cursor: "pointer",
                    textAlign: "left",
                    transition: "all 0.2s",
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                  }}
                  onMouseEnter={(e) => {
                    if (selectedClinicId !== clinic.id) {
                      e.currentTarget.style.borderColor = "#b0b0b0";
                      e.currentTarget.style.background = "#f9f9f9";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedClinicId !== clinic.id) {
                      e.currentTarget.style.borderColor = "#e0e0e0";
                      e.currentTarget.style.background = "#fff";
                    }
                  }}
                >
                  <div
                    style={{
                      width: "40px",
                      height: "40px",
                      borderRadius: "8px",
                      background: selectedClinicId === clinic.id ? "#1dd1a1" : "#00d4b3",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#fff",
                      fontSize: "18px",
                      fontWeight: "700",
                      flexShrink: 0,
                    }}
                  >
                    {(getClinicName(clinic) || "C").charAt(0).toUpperCase()}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        fontWeight: 600,
                        fontSize: "16px",
                        color: "#22313f",
                        marginBottom: "4px",
                      }}
                    >
                      {getClinicName(clinic)}
                    </div>
                    {clinic.address && (
                      <div
                        style={{
                          fontSize: "14px",
                          color: "#666",
                        }}
                      >
                        {clinic.address}
                      </div>
                    )}
                  </div>
                  {selectedClinicId === clinic.id && (
                    <div
                      style={{
                        width: "24px",
                        height: "24px",
                        borderRadius: "50%",
                        background: "#1dd1a1",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <span style={{ color: "#fff", fontSize: "16px" }}>✓</span>
                    </div>
                  )}
                </button>
              ))}
            </div>
          ) : (
            <div style={{ textAlign: "center", color: "#666", padding: "20px" }}>
              {isRTL ? "لا توجد عيادات متاحة" : "No clinics available"}
            </div>
          )}
        </div>

        {/* Footer Section - Fixed */}
        <div style={{ padding: "24px 32px 32px 32px", flexShrink: 0, borderTop: "1px solid #e0e0e0" }}>
          <button
            onClick={handleSelect}
            disabled={!selectedClinicId}
            style={{
              width: "100%",
              background: selectedClinicId ? "#1dd1a1" : "#ccc",
              color: "#fff",
              border: "none",
              borderRadius: 8,
              fontWeight: 600,
              fontSize: 17,
              padding: "12px 32px",
              cursor: selectedClinicId ? "pointer" : "not-allowed",
              transition: "background 0.2s",
            }}
          >
            {isRTL ? "تأكيد الاختيار" : "Confirm Selection"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SelectClinicModal;

